import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

import { useAuth } from "@/lib/auth";
import { fetchMyNotifications } from "@/lib/payreq";

interface NotificationsState {
  /** Unread in-app notifications — shown as a badge on the bell and the Alerts tab. */
  unreadCount: number;
  /** Re-fetch the unread count (e.g. after marking notifications read). */
  refresh: () => void;
  /** Optimistically set the count (the notifications page knows it after mark-read). */
  setUnreadCount: (n: number) => void;
}

const NotificationsContext = createContext<NotificationsState | null>(null);

/**
 * Shares the unread-notification count between the app bar bell and the bottom
 * "Alerts" tab. Light touch: one fetch on mount + a refetch on window focus —
 * no polling loop.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { token, ready } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const refresh = useCallback(() => {
    if (!token) return;
    fetchMyNotifications(token, { currentPage: 1, pageSize: 1 })
      .then((d) => setUnreadCount(d?.unreadCount ?? 0))
      .catch(() => {
        // Non-fatal — keep the last known badge.
      });
  }, [token]);

  useEffect(() => {
    if (!ready || !token) {
      setUnreadCount(0);
      return;
    }
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [ready, token, refresh]);

  return (
    <NotificationsContext.Provider value={{ unreadCount, refresh, setUnreadCount }}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}
